/**
 * IPUK Quick Add Modal
 * Handles variant selection and add-to-cart from collection pages
 */

(function() {
  'use strict';

  const IPUKQuickAdd = {
    modal: null,
    content: null,
    currentProduct: null,
    selectedOptions: {},
    
    init: function() {
      this.modal = document.getElementById('ipuk-quick-add-modal');
      if (!this.modal) return;
      
      this.content = this.modal.querySelector('.ipuk-modal__content');
      this.bindEvents();
    },
    
    bindEvents: function() {
      // Open modal
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-ipuk-quick-add]');
        if (trigger) {
          e.preventDefault();
          this.openModal(trigger.dataset.productHandle);
        }
      });
      
      // Close modal
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="close-ipuk-modal"]')) {
          this.closeModal();
        }
      });
      
      // ESC key closes modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.closeModal();
        }
      });
      
      // Quantity buttons
      this.modal.addEventListener('click', (e) => {
        const btn = e.target.closest('.ipuk-quantity-btn');
        if (btn) {
          this.handleQuantity(btn.dataset.action);
        }
      });
      
      // Option selection
      this.modal.addEventListener('click', (e) => {
        const optionBtn = e.target.closest('.ipuk-quick-add__option-btn');
        if (optionBtn && !optionBtn.classList.contains('is-disabled')) {
          this.selectOption(optionBtn);
        }
      });
      
      // Thumbnail click
      this.modal.addEventListener('click', (e) => {
        const thumb = e.target.closest('.ipuk-quick-add__thumb');
        if (thumb) {
          this.selectThumbnail(thumb);
        }
      });
      
      // Form submit
      this.modal.addEventListener('submit', (e) => {
        if (e.target.closest('.ipuk-quick-add__form')) {
          e.preventDefault();
          this.addToCart(e.target);
        }
      });
    },
    
    isOpen: function() {
      return this.modal.getAttribute('aria-hidden') === 'false';
    },
    
    openModal: function(productHandle) {
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-loading');
      document.body.classList.add('ipuk-modal-open');
      
      this.loadProduct(productHandle);
    },
    
    closeModal: function() {
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ipuk-modal-open');
      
      // Clear content after animation
      setTimeout(() => {
        this.content.innerHTML = '';
        this.currentProduct = null;
        this.selectedOptions = {};
      }, 300);
    },
    
    loadProduct: function(handle) {
      fetch('/products/' + handle + '.js')
        .then(res => res.json())
        .then(product => {
          this.currentProduct = product;
          this.renderProduct(product);
          this.modal.classList.remove('is-loading');
        })
        .catch(err => {
          console.error('Failed to load product:', err);
          this.content.innerHTML = '<p style="padding: 40px; text-align: center;">Failed to load product. Please try again.</p>';
          this.modal.classList.remove('is-loading');
        });
    },
    
    // Helper to get option name (handles both string and object formats)
    getOptionName: function(option) {
      return typeof option === 'object' ? option.name : option;
    },
    
    renderProduct: function(product) {
      const firstVariant = product.variants[0];
      
      // Initialize selected options
      product.options.forEach((option, index) => {
        const optionName = this.getOptionName(option);
        this.selectedOptions[optionName] = firstVariant['option' + (index + 1)];
      });
      
      const html = 
        '<div class="ipuk-quick-add">' +
          '<div class="ipuk-quick-add__grid">' +
            '<div class="ipuk-quick-add__images">' +
              '<div class="ipuk-quick-add__main-image">' +
                '<img src="' + this.getImageUrl(product.featured_image, '600x') + '" ' +
                     'alt="' + this.escapeHtml(product.title) + '" ' +
                     'class="ipuk-quick-add__img" ' +
                     'id="ipuk-main-image">' +
              '</div>' +
              this.renderThumbnails(product) +
            '</div>' +
            
            '<div class="ipuk-quick-add__info">' +
              '<h2 id="ipuk-modal-title" class="ipuk-quick-add__title">' + this.escapeHtml(product.title) + '</h2>' +
              
              '<div class="ipuk-quick-add__price" id="ipuk-price">' +
                this.renderPrice(firstVariant) +
              '</div>' +
              
              '<div class="ipuk-quick-add__availability ' + (firstVariant.available ? 'in-stock' : 'out-of-stock') + '" id="ipuk-availability">' +
                (firstVariant.available ? '✓ In Stock' : '✕ Out of Stock') +
              '</div>' +
              
              '<form class="ipuk-quick-add__form" data-product-id="' + product.id + '">' +
                '<input type="hidden" name="id" value="' + firstVariant.id + '" class="ipuk-variant-id">' +
                
                '<div class="ipuk-quick-add__options">' +
                  this.renderOptions(product) +
                '</div>' +
                
                '<div class="ipuk-quick-add__quantity">' +
                  '<label for="ipuk-quantity">Quantity:</label>' +
                  '<div class="ipuk-quantity-selector">' +
                    '<button type="button" class="ipuk-quantity-btn" data-action="decrease">−</button>' +
                    '<input type="number" ' +
                           'id="ipuk-quantity" ' +
                           'name="quantity" ' +
                           'value="1" ' +
                           'min="1" ' +
                           'class="ipuk-quantity-input">' +
                    '<button type="button" class="ipuk-quantity-btn" data-action="increase">+</button>' +
                  '</div>' +
                '</div>' +
                
                '<button type="submit" ' +
                        'class="ipuk-quick-add__submit button button--primary" ' +
                        (firstVariant.available ? '' : 'disabled') + '>' +
                  '<span class="ipuk-quick-add__submit-text">' + (firstVariant.available ? 'Add to Cart' : 'Out of Stock') + '</span>' +
                  '<span class="ipuk-quick-add__submit-loading">' +
                    '<svg class="spinner" viewBox="0 0 50 50">' +
                      '<circle cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>' +
                    '</svg>' +
                  '</span>' +
                '</button>' +
                
                '<a href="/products/' + product.handle + '" class="ipuk-quick-add__view-full">' +
                  'View Full Details →' +
                '</a>' +
              '</form>' +
            '</div>' +
          '</div>' +
        '</div>';
      
      this.content.innerHTML = html;
    },
    
    renderThumbnails: function(product) {
      if (!product.images || product.images.length <= 1) return '';
      
      var self = this;
      var thumbs = product.images.slice(0, 6).map(function(img, i) {
        return '<button type="button" ' +
                'class="ipuk-quick-add__thumb ' + (i === 0 ? 'is-active' : '') + '" ' +
                'data-image-src="' + self.getImageUrl(img, '600x') + '">' +
          '<img src="' + self.getImageUrl(img, '100x') + '" alt="">' +
        '</button>';
      }).join('');
      
      return '<div class="ipuk-quick-add__thumbnails">' + thumbs + '</div>';
    },
    
    renderOptions: function(product) {
      var self = this;
      var firstOptionName = this.getOptionName(product.options[0]);
      
      if (product.options.length === 1 && firstOptionName === 'Title') {
        return ''; // No options for single variant
      }
      
      return product.options.map(function(option, optionIndex) {
        var optionName = self.getOptionName(option);
        var values = self.getUniqueOptionValues(product, optionIndex);
        
        var valueButtons = values.map(function(value) {
          var isSelected = self.selectedOptions[optionName] === value;
          var isAvailable = self.isOptionAvailable(product, optionIndex, value);
          return '<button type="button" ' +
                  'class="ipuk-quick-add__option-btn ' + (isSelected ? 'is-selected' : '') + ' ' + (!isAvailable ? 'is-disabled' : '') + '" ' +
                  'data-option-name="' + self.escapeHtml(optionName) + '" ' +
                  'data-option-value="' + self.escapeHtml(value) + '">' +
                  self.escapeHtml(value) +
                '</button>';
        }).join('');
        
        return '<div class="ipuk-quick-add__option-group" data-option-index="' + optionIndex + '">' +
            '<span class="ipuk-quick-add__option-label">' + self.escapeHtml(optionName) + ':</span>' +
            '<div class="ipuk-quick-add__option-values">' +
              valueButtons +
            '</div>' +
          '</div>';
      }).join('');
    },
    
    getUniqueOptionValues: function(product, optionIndex) {
      var values = [];
      var seen = {};
      product.variants.forEach(function(variant) {
        var val = variant['option' + (optionIndex + 1)];
        if (!seen[val]) {
          seen[val] = true;
          values.push(val);
        }
      });
      return values;
    },
    
    isOptionAvailable: function(product, optionIndex, value) {
      var self = this;
      // Check if any variant with this option value is available
      return product.variants.some(function(variant) {
        if (variant['option' + (optionIndex + 1)] !== value) return false;
        
        // Check if other selected options match
        var matches = true;
        product.options.forEach(function(opt, i) {
          var optName = self.getOptionName(opt);
          if (i !== optionIndex && self.selectedOptions[optName]) {
            if (variant['option' + (i + 1)] !== self.selectedOptions[optName]) {
              matches = false;
            }
          }
        });
        
        return matches && variant.available;
      });
    },
    
    selectOption: function(btn) {
      var optionName = btn.dataset.optionName;
      var optionValue = btn.dataset.optionValue;
      
      // Update selected options
      this.selectedOptions[optionName] = optionValue;
      
      // Update button states
      var group = btn.closest('.ipuk-quick-add__option-group');
      group.querySelectorAll('.ipuk-quick-add__option-btn').forEach(function(b) {
        b.classList.remove('is-selected');
      });
      btn.classList.add('is-selected');
      
      // Find matching variant
      var variant = this.findMatchingVariant();
      if (variant) {
        this.updateForVariant(variant);
      }
      
      // Update available options
      this.updateAvailableOptions();
    },
    
    findMatchingVariant: function() {
      var self = this;
      var product = this.currentProduct;
      
      return product.variants.find(function(variant) {
        var matches = true;
        product.options.forEach(function(opt, i) {
          var optName = self.getOptionName(opt);
          if (variant['option' + (i + 1)] !== self.selectedOptions[optName]) {
            matches = false;
          }
        });
        return matches;
      });
    },
    
    updateForVariant: function(variant) {
      // Update hidden input
      this.modal.querySelector('.ipuk-variant-id').value = variant.id;
      
      // Update price
      var priceEl = this.modal.querySelector('#ipuk-price');
      priceEl.innerHTML = this.renderPrice(variant);
      
      // Update availability
      var availEl = this.modal.querySelector('#ipuk-availability');
      availEl.className = 'ipuk-quick-add__availability ' + (variant.available ? 'in-stock' : 'out-of-stock');
      availEl.textContent = variant.available ? '✓ In Stock' : '✕ Out of Stock';
      
      // Update button
      var submitBtn = this.modal.querySelector('.ipuk-quick-add__submit');
      var submitText = submitBtn.querySelector('.ipuk-quick-add__submit-text');
      submitBtn.disabled = !variant.available;
      submitText.textContent = variant.available ? 'Add to Cart' : 'Out of Stock';
      
      // Update image if variant has one
      if (variant.featured_image) {
        var mainImg = this.modal.querySelector('#ipuk-main-image');
        mainImg.src = this.getImageUrl(variant.featured_image.src, '600x');
      }
    },
    
    updateAvailableOptions: function() {
      var self = this;
      var product = this.currentProduct;
      
      product.options.forEach(function(option, optionIndex) {
        var group = self.modal.querySelector('[data-option-index="' + optionIndex + '"]');
        if (!group) return;
        
        group.querySelectorAll('.ipuk-quick-add__option-btn').forEach(function(btn) {
          var value = btn.dataset.optionValue;
          var isAvailable = self.isOptionAvailable(product, optionIndex, value);
          btn.classList.toggle('is-disabled', !isAvailable);
        });
      });
    },
    
    renderPrice: function(variant) {
      var formatMoney = function(cents) {
        return '£' + (cents / 100).toFixed(2);
      };
      
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        return '<span class="price--highlight">' + formatMoney(variant.price) + '</span>' +
          '<span class="price--compare">' + formatMoney(variant.compare_at_price) + '</span>';
      }
      return '<span>' + formatMoney(variant.price) + '</span>';
    },
    
    selectThumbnail: function(thumb) {
      // Update active state
      this.modal.querySelectorAll('.ipuk-quick-add__thumb').forEach(function(t) {
        t.classList.remove('is-active');
      });
      thumb.classList.add('is-active');
      
      // Update main image
      var mainImg = this.modal.querySelector('#ipuk-main-image');
      mainImg.src = thumb.dataset.imageSrc;
    },
    
    handleQuantity: function(action) {
      var input = this.modal.querySelector('.ipuk-quantity-input');
      var value = parseInt(input.value) || 1;
      
      if (action === 'increase') {
        value++;
      } else if (action === 'decrease' && value > 1) {
        value--;
      }
      
      input.value = value;
    },
    
    addToCart: function(form) {
      var self = this;
      var submitBtn = form.querySelector('.ipuk-quick-add__submit');
      var variantId = form.querySelector('.ipuk-variant-id').value;
      var quantity = form.querySelector('.ipuk-quantity-input').value;
      
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: parseInt(quantity)
        })
      })
      .then(function(res) {
        if (!res.ok) throw new Error('Failed to add to cart');
        return res.json();
      })
      .then(function(item) {
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.add('is-success');
        submitBtn.querySelector('.ipuk-quick-add__submit-text').textContent = '✓ Added!';
        
        // Refresh cart count
        self.updateCartCount();
        
        // Close modal after delay
        setTimeout(function() {
          self.closeModal();
          
          // Open cart drawer if theme supports it
          if (window.theme && window.theme.cartType === 'drawer') {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true
            }));
          }
        }, 1000);
      })
      .catch(function(err) {
        console.error('Add to cart error:', err);
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = false;
        submitBtn.querySelector('.ipuk-quick-add__submit-text').textContent = 'Error - Try Again';
        
        setTimeout(function() {
          submitBtn.querySelector('.ipuk-quick-add__submit-text').textContent = 'Add to Cart';
        }, 2000);
      });
    },
    
    updateCartCount: function() {
      fetch('/cart.js')
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          // Update cart count in header
          var countEls = document.querySelectorAll('.header__cart-count, [data-cart-count]');
          countEls.forEach(function(el) {
            el.textContent = cart.item_count;
          });
          
          // Trigger cart refresh event for theme
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { scrollToTop: false }
          }));
        });
    },
    
    getImageUrl: function(src, size) {
      if (!src) return '';
      
      // Handle Shopify CDN URLs
      if (src.indexOf('cdn.shopify.com') !== -1) {
        return src.replace(/(_\d+x\d*|\d+x\d*)?(\.[a-z]+)(\?.*)?$/i, '_' + size + '$2');
      }
      
      // For product.js URLs, build the correct format
      return src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_' + size + '.$1');
    },
    
    escapeHtml: function(str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  };
  
  // Direct Add to Cart for single variant products
  document.addEventListener('click', function(e) {
    var directAddBtn = e.target.closest('[data-ipuk-direct-add]');
    if (!directAddBtn) return;
    
    e.preventDefault();
    
    var variantId = directAddBtn.dataset.variantId;
    var originalText = directAddBtn.textContent;
    
    directAddBtn.disabled = true;
    directAddBtn.textContent = 'Adding...';
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Failed');
      return res.json();
    })
    .then(function() {
      directAddBtn.textContent = '✓ Added!';
      
      // Update cart
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          document.querySelectorAll('.header__cart-count, [data-cart-count]').forEach(function(el) {
            el.textContent = cart.item_count;
          });
          
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { scrollToTop: false }
          }));
        });
      
      setTimeout(function() {
        directAddBtn.disabled = false;
        directAddBtn.textContent = originalText;
      }, 2000);
    })
    .catch(function() {
      directAddBtn.textContent = 'Error';
      setTimeout(function() {
        directAddBtn.disabled = false;
        directAddBtn.textContent = originalText;
      }, 2000);
    });
  });
  
  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { IPUKQuickAdd.init(); });
  } else {
    IPUKQuickAdd.init();
  }
})();
